import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import {
  AEGIS_PREVIEW_CHANNEL,
  isAegisPreviewMessage,
  type AegisPreviewEvent,
  type AegisPreviewRenderRequest,
} from '@move-right/aegis/browser/previewProtocol';

import { environment } from '../../../environments/environment';

const PREVIEW_URL = `/assets/aegis-preview/preview.html?v=${encodeURIComponent(environment.version)}`;

@Component({
  selector: 'app-aegis-document-preview',
  standalone: true,
  template: '<iframe #frame [src]="src" title="Document preview"></iframe>',
  styles: [ `
    :host { display: block; }
    iframe { display: block; width: 100%; height: 100%; border: 0; }
  ` ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AegisDocumentPreviewComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild('frame', { static: true }) frame: ElementRef<HTMLIFrameElement>;

  @Input() templateSource: string | undefined;

  @Input() context: unknown;

  @Output() renderFailed = new EventEmitter<string>();

  @Output() rendered = new EventEmitter<void>();

  public src: SafeResourceUrl;

  private ready = false;

  private nextId = 1;

  private lastSentId = 0;

  constructor(
    private zone: NgZone,
    private host: ElementRef<HTMLElement>,
    sanitizer: DomSanitizer,
  ) {
    this.src = sanitizer.bypassSecurityTrustResourceUrl(PREVIEW_URL);
  }

  ngOnInit() {
    this.zone.runOutsideAngular(() => window.addEventListener('message', this.onMessage));
  }

  ngOnChanges() {
    this.post();
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.onMessage);
  }

  private post() {
    const target = this.frame?.nativeElement.contentWindow;
    if (!this.ready || !target || !this.templateSource || !this.context) return;
    this.lastSentId = this.nextId++;
    const request: AegisPreviewRenderRequest = {
      channel: AEGIS_PREVIEW_CHANNEL,
      type: 'render',
      id: this.lastSentId,
      templateSource: this.templateSource,
      context: this.context,
      activeRole: 'customer',
    };
    target.postMessage(request, window.location.origin);
  }

  private onMessage = (event: MessageEvent) => {
    if (event.source !== this.frame.nativeElement.contentWindow || event.origin !== window.location.origin) return;
    if (!isAegisPreviewMessage(event.data)) return;
    const message = event.data as AegisPreviewEvent;
    if (message.type === 'ready') {
      this.ready = true;
      this.post();
    } else if (message.type === 'resized') {
      this.host.nativeElement.style.setProperty('--content-height', `${message.height}px`);
    } else if (message.type === 'rendered' && message.id === this.lastSentId) {
      this.zone.run(() => this.rendered.emit());
    } else if (message.type === 'error' && message.id === this.lastSentId) {
      this.zone.run(() => this.renderFailed.emit(message.message));
    }
  };
}
